import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, BookOpen, Lightbulb, CheckCircle2, AlertTriangle,
  FileText, Video, Users, Target, ExternalLink, ChevronRight,
  Download, Play, BookMarked, Network
} from 'lucide-react';
import type { EnhancedFrameworkEntry } from '../types/enhancedFramework';
import { EvidenceContinuumDiagram } from '../components/visual/EvidenceContinuumDiagram';
import { masterRegistryEntries } from '../data/masterRegistryEntries';

interface EnhancedFrameworkEntryPageProps {
  framework: EnhancedFrameworkEntry;
}

export const EnhancedFrameworkEntryPage: React.FC<EnhancedFrameworkEntryPageProps> = ({ framework }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'how-to-use' | 'resources' | 'learn'>('overview');
  
  // Helper to get canonical name from framework ID
  const getFrameworkName = (frameworkId: string): string => {
    const entry = masterRegistryEntries.find(e => e.encodedId === frameworkId);
    return entry?.canonicalName || frameworkId;
  };

  // Get type badge styling
  const getTypeBadge = () => {
    const styles = {
      'domain-expertise': 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
      'clarity-alignment': 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
      'checklist-completeness': 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]'
    };
    
    const labels = {
      'domain-expertise': 'Domain Expertise',
      'clarity-alignment': 'Clarity & Alignment',
      'checklist-completeness': 'Checklist/Completeness'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 ${styles[framework.frameworkType]}`}>
        {labels[framework.frameworkType]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div 
        className="border-b-4"
        style={{
          backgroundColor: framework.visual.onBrandElements.backgroundColor,
          borderColor: framework.visual.onBrandElements.accentColor
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <Link 
            to="/elevation/models" 
            className="inline-flex items-center gap-2 text-sm text-charcoal-600 hover:text-charcoal-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Models
          </Link>

          {/* Title */}
          <div className="flex items-start gap-4 mb-4">
            <div className="text-5xl">{framework.visual.icon}</div>
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-charcoal-900 mb-2">
                {framework.canonicalName}
              </h1>
              {framework.aliases.length > 0 && (
                <p className="text-sm text-charcoal-600 mb-3">
                  Also known as: {framework.aliases.join(', ')}
                </p>
              )}
              <div className="flex items-center gap-3">
                {getTypeBadge()}
                <span className="text-sm text-charcoal-600">
                  {framework.learning.timeToLearn} min to learn
                </span>
                <span className="text-sm text-charcoal-600">
                  {'⭐'.repeat(framework.learning.difficulty)}
                </span>
              </div>
            </div>
          </div>

          {/* Multi-Resolution Content Display */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column: Phrase & Sentence */}
            <div className="space-y-4">
              <div className="bg-[var(--bg-raised)] p-5 border-2 border-sage-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📌</span>
                  <h3 className="text-sm font-semibold text-charcoal-600 uppercase tracking-wide">Phrase</h3>
                </div>
                <p className="text-xl font-semibold text-sage-700 leading-tight">
                  {framework.content.phrase}
                </p>
              </div>

              <div className="bg-[var(--bg-raised)] p-5 border-2 border-consulting-slate-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📝</span>
                  <h3 className="text-sm font-semibold text-charcoal-600 uppercase tracking-wide">Sentence</h3>
                </div>
                <p className="text-base text-charcoal-800 leading-relaxed">
                  {framework.content.sentence}
                </p>
              </div>
            </div>

            {/* Right Column: Paragraph */}
            <div className="bg-[var(--bg-raised)] p-5 border-2 border-[var(--mustard)] shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">📄</span>
                <h3 className="text-sm font-semibold text-charcoal-600 uppercase tracking-wide">Paragraph</h3>
              </div>
              <p className="text-base text-charcoal-700 leading-relaxed">
                {framework.content.paragraph}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b-2 border-charcoal-200">
          {[
            { id: 'overview', label: 'Overview', icon: BookOpen },
            { id: 'how-to-use', label: 'How to Use', icon: Target },
            { id: 'resources', label: 'Resources', icon: FileText },
            { id: 'learn', label: 'Learn & Practice', icon: BookMarked }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'text-consulting-slate-blue-600 border-consulting-slate-blue-600'
                  : 'text-charcoal-600 border-transparent hover:text-charcoal-900'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Relevance Framework (What/Why/How) */}
            <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 p-6 border-2 border-sage-200">
              <h2 className="text-2xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
                <Lightbulb className="h-6 w-6 text-sage-600" />
                What / Why / How
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-bold text-sage-900 mb-2">What is it?</h3>
                  <p className="text-sm text-charcoal-700">{framework.relevance.what}</p>
                </div>
                <div>
                  <h3 className="font-bold text-sage-900 mb-2">Why is it important?</h3>
                  <p className="text-sm text-charcoal-700">{framework.relevance.why}</p>
                </div>
                <div>
                  <h3 className="font-bold text-sage-900 mb-2">How does it work?</h3>
                  <p className="text-sm text-charcoal-700">{framework.relevance.how}</p>
                </div>
              </div>
            </div>

            {/* Primary Visual */}
            {framework.visual.primaryDiagram.svgComponent === 'EvidenceContinuumDiagram' && (
              <div className="bg-[var(--bg-raised)] p-6 border-2 border-charcoal-200">
                <EvidenceContinuumDiagram showLabels={true} size="large" />
              </div>
            )}

            {/* Framework Structure (Questions) */}
            {framework.structure?.questions && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4">
                  The Seven Questions
                </h2>
                <div className="space-y-4">
                  {framework.structure.questions.map(q => (
                    <div key={q.number} className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200 hover:border-consulting-slate-blue-300 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-consulting-slate-blue-600 text-white flex items-center justify-center font-bold">
                          {q.number}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-charcoal-900 mb-1">{q.question}</h3>
                          <p className="text-sm text-consulting-slate-blue-700 mb-2 italic">{q.intent}</p>
                          <p className="text-sm text-charcoal-700 mb-3">{q.guidance}</p>
                          {q.examples && q.examples.length > 0 && (
                            <details className="text-sm">
                              <summary className="cursor-pointer text-consulting-slate-blue-600 hover:text-consulting-slate-blue-800 font-medium">
                                See examples →
                              </summary>
                              <ul className="mt-2 space-y-1 pl-4">
                                {q.examples.map((ex, idx) => (
                                  <li key={idx} className={ex.startsWith('✓') ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}>
                                    {ex}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Critical Callouts */}
            {framework.resources.criticalCallouts.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-6 flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6 text-[var(--mustard)]" />
                  Critical Guidelines & Common Mistakes
                </h2>
                <div className="space-y-3">
                  {framework.resources.criticalCallouts.map(callout => (
                    <div 
                      key={callout.id}
                      className="bg-[var(--bg-raised)] p-4 border-2 border-charcoal-200 border-l-4 border-l-consulting-slate-blue-400 hover:border-consulting-slate-blue-300 transition-colors shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-xl flex-shrink-0">{callout.icon}</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-charcoal-900 mb-1">{callout.title}</h3>
                          <p className="text-sm text-charcoal-600 leading-relaxed">{callout.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Relationships */}
            {(framework.relationships.precededBy.length > 0 || 
              framework.relationships.followedBy.length > 0 ||
              framework.relationships.usedIn.length > 0) && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
                  <Network className="h-6 w-6 text-consulting-slate-blue-600" />
                  How This Connects
                </h2>
                <div className="space-y-3">
                  {framework.relationships.precededBy.length > 0 && (
                    <div className="bg-[var(--bg-raised)] p-4 border-2 border-charcoal-200 border-l-4 border-l-amber-400 shadow-sm">
                      <h3 className="font-semibold text-charcoal-900 mb-3 flex items-center gap-2">
                        <span className="text-[var(--mustard)]">←</span> Preceded by:
                      </h3>
                      {framework.relationships.precededBy.map((rel, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 text-charcoal-400 mt-1 flex-shrink-0" />
                          <div>
                            <Link to={`/elevation/entry/${rel.frameworkId}`} className="font-semibold text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 hover:underline">
                              {getFrameworkName(rel.frameworkId)}
                            </Link>
                            <p className="text-sm text-charcoal-600 mt-1">{rel.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {framework.relationships.usedIn.length > 0 && (
                    <div className="bg-[var(--bg-raised)] p-4 border-2 border-charcoal-200 border-l-4 border-l-sage-400 shadow-sm">
                      <h3 className="font-semibold text-charcoal-900 mb-3">Used in:</h3>
                      {framework.relationships.usedIn.map((rel, idx) => (
                        <div key={idx} className="mb-3 last:mb-0 flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 text-charcoal-400 mt-1 flex-shrink-0" />
                          <div>
                            <Link to={`/elevation/entry/${rel.frameworkId}`} className="font-semibold text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 hover:underline">
                              {getFrameworkName(rel.frameworkId)}
                              {rel.stage && <span className="text-charcoal-500"> • {rel.stage} stage</span>}
                            </Link>
                            <p className="text-sm text-charcoal-600 mt-1">{rel.purpose}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'how-to-use' && (
          <div className="space-y-8">
            {/* Templates */}
            {framework.resources.templates.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Templates</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {framework.resources.templates.map(template => (
                    <div key={template.id} className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-charcoal-900">{template.name}</h3>
                          <p className="text-sm text-charcoal-600">{template.description}</p>
                        </div>
                        <Download className="h-5 w-5 text-consulting-slate-blue-600" />
                      </div>
                      {template.content && (
                        <details className="text-sm">
                          <summary className="cursor-pointer text-consulting-slate-blue-600 hover:text-consulting-slate-blue-800 font-medium mb-2">
                            View template →
                          </summary>
                          <pre className="bg-charcoal-50 p-3 rounded overflow-x-auto text-xs">
                            {template.content}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Facilitation Guides */}
            {framework.resources.facilitationGuides.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Facilitation Guide</h2>
                {framework.resources.facilitationGuides.map((guide, idx) => (
                  <div key={idx} className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 p-6 border-2 border-consulting-slate-blue-200">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-charcoal-900 mb-2">{guide.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-charcoal-700">
                        <span>⏱️ {guide.duration} minutes</span>
                        <span>👥 Roles: {guide.roles.join(', ')}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {guide.steps.map(step => (
                        <div key={step.stepNumber} className="bg-[var(--bg-raised)] p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-consulting-slate-blue-600 text-white flex items-center justify-center text-sm font-bold">
                              {step.stepNumber}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-1">
                                <h4 className="font-bold text-charcoal-900">{step.title}</h4>
                                <span className="text-xs text-charcoal-600">{step.duration} min</span>
                              </div>
                              <p className="text-sm text-charcoal-700 mb-2">{step.instructions}</p>
                              {step.tips && step.tips.length > 0 && (
                                <div className="mt-2 bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)] p-2">
                                  <p className="text-xs font-semibold text-[var(--mustard)] mb-1">💡 Tips:</p>
                                  <ul className="text-xs text-[var(--mustard)] space-y-1">
                                    {step.tips.map((tip, tipIdx) => (
                                      <li key={tipIdx}>• {tip}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Instructions for Individual/Team Use */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200">
                <h3 className="font-bold text-charcoal-900 mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-consulting-slate-blue-600" />
                  For Individual Use
                </h3>
                <div className="prose prose-sm text-charcoal-700 max-w-none">
                  <ReactMarkdown>{framework.resources.instructions.individual}</ReactMarkdown>
                </div>
              </div>
              
              <div className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200">
                <h3 className="font-bold text-charcoal-900 mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-sage-600" />
                  For Team Use
                </h3>
                <div className="prose prose-sm text-charcoal-700 max-w-none">
                  <ReactMarkdown>{framework.resources.instructions.team}</ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="space-y-8">
            {/* Media Resources */}
            {framework.resources.media.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Videos & Articles</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {framework.resources.media.map(media => (
                    <div key={media.id} className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200 hover:border-consulting-slate-blue-300 transition-colors">
                      <div className="flex items-start gap-3">
                        {media.type === 'video' && <Video className="h-6 w-6 text-[var(--coral)] flex-shrink-0" />}
                        {media.type === 'article' && <FileText className="h-6 w-6 text-[var(--text-ter)] flex-shrink-0" />}
                        <div className="flex-1">
                          <h3 className="font-bold text-charcoal-900 mb-1">{media.title}</h3>
                          <p className="text-sm text-charcoal-600 mb-2">{media.description}</p>
                          {media.duration && (
                            <span className="text-xs text-charcoal-500">{media.duration} min</span>
                          )}
                          <a 
                            href={media.url}
                            className="mt-2 inline-flex items-center gap-1 text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-800 font-medium"
                          >
                            {media.type === 'video' ? <Play className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
                            {media.type === 'video' ? 'Watch' : 'Read'}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Common Missteps */}
            {framework.quality.commonMissteps.missteps.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Common Missteps & How to Fix Them</h2>
                <div className="space-y-3">
                  {framework.quality.commonMissteps.missteps.map((misstep, idx) => (
                    <div key={idx} className="bg-[var(--bg-raised)] p-5 border-l-4 border-[var(--coral)]">
                      <div className="mb-2">
                        <span className="inline-block px-2 py-1 bg-[var(--coral-dim)] text-[var(--coral)] text-xs font-semibold rounded mb-2">
                          {misstep.relatedQuestion}
                        </span>
                        <h3 className="font-bold text-[var(--coral)]">❌ {misstep.mistake}</h3>
                      </div>
                      <p className="text-sm text-charcoal-700 mb-2">
                        <span className="font-semibold">Consequence:</span> {misstep.consequence}
                      </p>
                      <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] p-2 rounded">
                        <span className="font-semibold">✓ Fix:</span> {misstep.fix}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quality Checklist */}
            {framework.quality.checklist.items.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Quality Checklist</h2>
                <div className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200">
                  <div className="space-y-3">
                    {framework.quality.checklist.items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 pb-3 border-b border-charcoal-100 last:border-0">
                        <CheckCircle2 className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-charcoal-900">{item.criterion}</p>
                          {item.guidance && (
                            <p className="text-sm text-charcoal-600 mt-1">{item.guidance}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'learn' && (
          <div className="space-y-8">
            {/* Prerequisites */}
            {framework.learning.prerequisites.length > 0 && (
              <div className="bg-[var(--slate-dim)] p-5 border-2 border-[var(--slate)]">
                <h3 className="font-bold text-[var(--text-sec)] mb-3">📚 Prerequisites</h3>
                <div className="space-y-2">
                  {framework.learning.prerequisites.map((prereq, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <ChevronRight className="h-4 w-4 text-[var(--text-ter)] mt-1" />
                      <div>
                        <Link to={`/elevation/entry/${prereq.frameworkId}`} className="font-semibold text-[var(--text-sec)] hover:text-[var(--text-pri)]">
                          {getFrameworkName(prereq.frameworkId)}
                        </Link>
                        <p className="text-sm text-[var(--text-sec)]">{prereq.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Practice Exercises */}
            {framework.learning.practiceExercises && framework.learning.practiceExercises.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Practice Exercises</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  {framework.learning.practiceExercises.map((exercise, idx) => (
                    <div key={idx} className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200">
                      <div className="mb-3">
                        <h3 className="font-bold text-charcoal-900 mb-1">{exercise.title}</h3>
                        <div className="flex items-center gap-2 text-xs text-charcoal-600">
                          <span>⏱️ {exercise.duration} min</span>
                          <span>{'⭐'.repeat(exercise.difficulty)}</span>
                        </div>
                      </div>
                      <p className="text-sm text-charcoal-700">{exercise.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Courses & Learning Paths */}
            {(framework.learning.courses || framework.learning.learningPaths) && (
              <div className="grid md:grid-cols-2 gap-6">
                {framework.learning.courses && framework.learning.courses.length > 0 && (
                  <div className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200">
                    <h3 className="font-bold text-charcoal-900 mb-3">📖 Courses</h3>
                    <div className="space-y-2">
                      {framework.learning.courses.map((course, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 text-consulting-slate-blue-600 mt-1" />
                          <div>
                            <Link to={`/courses/${course.courseId}`} className="font-semibold text-consulting-slate-blue-700 hover:text-consulting-slate-blue-900">
                              {course.courseName}
                            </Link>
                            {course.moduleNumber && (
                              <p className="text-xs text-charcoal-600">Module {course.moduleNumber}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {framework.learning.learningPaths && framework.learning.learningPaths.length > 0 && (
                  <div className="bg-[var(--bg-raised)] p-5 border-2 border-charcoal-200">
                    <h3 className="font-bold text-charcoal-900 mb-3">🛤️ Learning Paths</h3>
                    <div className="space-y-2">
                      {framework.learning.learningPaths.map((path, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 text-sage-600 mt-1" />
                          <div>
                            <Link to={`/paths/${path.pathId}`} className="font-semibold text-sage-700 hover:text-sage-900">
                              {path.pathName}
                            </Link>
                            <p className="text-xs text-charcoal-600">Step {path.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

